import { Injectable, signal, computed, effect, WritableSignal, NgZone, inject } from '@angular/core';
import { Router } from "@angular/router";
import { toObservable } from '@angular/core/rxjs-interop';
import { User } from './user.model';

// Import services that need to be reset on logout
import { UserHistoryService } from '../../features/home/services/user-history.service';

// Firebase auth imports
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  deleteUser,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  User as FirebaseUser,
  browserLocalPersistence,
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  getMultiFactorResolver,
  MultiFactorResolver,
  MultiFactorError
} from '@angular/fire/auth';

/**
 * Authentication service using Angular 19 Signal API with Firebase Authentication
 * Manages user authentication state and Firebase operations
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Primary authentication state signals
  public readonly profile: WritableSignal<User | null> = signal<User | null>(null);
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _isLoading = signal<boolean>(true); // Start as loading
  private readonly _error = signal<string | null>(null);
  private returnUrl: string = '/';

  // Public signals (read-only API)
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed signals for derived state
  readonly userEmail = computed(() => this.profile()?.email ?? null);
  readonly userName = computed(() => this.profile()?.name ?? null);
  readonly userPicture = computed(() => this.profile()?.picture ?? null);
  readonly userDomain = computed(() => this.profile()?.domain ?? null);

  // Observable interface for compatibility with existing code
  readonly profile$ = toObservable(this.profile);
  readonly isAuthenticated$ = toObservable(this._isAuthenticated);
  readonly isLoading$ = toObservable(this._isLoading);

  // MFA state
  private readonly _mfaRequired = signal<boolean>(false);
  readonly mfaRequired = this._mfaRequired.asReadonly();
  private pendingMfaResolver: MultiFactorResolver | null = null;
  private postMfaReturnUrl: string = '/';

  // Auth providers
  private googleProvider = new GoogleAuthProvider();
  private microsoftProvider = new OAuthProvider('microsoft.com');

  // Services for state reset
  private userHistoryService!: UserHistoryService;

  constructor(
    private auth: Auth,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Inject services for state reset on logout
    this.userHistoryService = inject(UserHistoryService);
    // Configure Microsoft provider
    this.microsoftProvider.setCustomParameters({
      prompt: 'select_account',
      tenant: 'common' // Use 'common' for any Microsoft account, or your Azure AD tenant ID
    });

    this._isLoading.set(true);

    // Defer Firebase initialization to avoid injection context issues
    this.ngZone.run(() => {
      // Use setTimeout to ensure Angular is fully initialized
      setTimeout(() => {
        this.initializeFirebase();
      }, 0);
    });

    // Log authentication state changes
    effect(() => {
      const isAuthenticated = this._isAuthenticated();
      console.log(`Authentication state changed: ${isAuthenticated ? 'authenticated' : 'not authenticated'}`);
    });
  }

  /**
   * Initialize Firebase authentication with persistence and listeners
   */
  private initializeFirebase(): void {
    // Set persistence using Auth instance method - this is the correct way
    this.auth.setPersistence(browserLocalPersistence)
      .then(() => {
        console.log('Auth persistence set to local');
        this.setupAuthListeners();
      })
      .catch(error => {
        console.error('Error setting persistence:', error);
        // Still setup listeners even if persistence fails
        this.setupAuthListeners();
      });
  }

  /**
   * Set up Firebase auth state listeners
   */
  private setupAuthListeners(): void {
    // Listen for auth state changes - Firebase will manage these properly within Angular context
    onAuthStateChanged(this.auth, (user) => {
      // Ensure Angular is aware of the state change
      this.ngZone.run(() => {
        console.log('Auth state changed:', user ? 'user exists' : 'no user');
        this.handleAuthState(user);
      });
    });

    // Also listen for token changes to ensure we have the latest auth state
    onIdTokenChanged(this.auth, (user) => {
      // Ensure Angular is aware of the token change
      this.ngZone.run(() => {
        console.log('Token changed:', user ? 'token exists' : 'no token');
        this.handleAuthState(user);
      });
    });
  }

  /**
   * Handle auth state changes from both observers
   */
  private handleAuthState(firebaseUser: FirebaseUser | null): void {
    if (firebaseUser) {
      if (this.isEmailPasswordUser(firebaseUser) && !firebaseUser.emailVerified) {
        // Email/password user not yet verified: do not authenticate or navigate.
        // Sign-out is handled by the registration/login flow.
        this._isAuthenticated.set(false);
        this.profile.set(null);
        this._isLoading.set(false);
        return;
      }
      this.handleSuccessfulAuth(firebaseUser);
    } else {
      this._isAuthenticated.set(false);
      this.profile.set(null);
    }
    this._isLoading.set(false);
  }

  /**
   * Handle successful authentication
   */
  private async handleSuccessfulAuth(firebaseUser: FirebaseUser): Promise<void> {
    try {
      const user = this.mapFirebaseUserToUser(firebaseUser);
      this.profile.set(user);
      this._isAuthenticated.set(true);
      this._error.set(null);

      // Force MFA enrollment for email/password users without enrolled factors
      const enrolled = multiFactor(firebaseUser).enrolledFactors.length > 0;
      if (this.isEmailPasswordUser(firebaseUser) && !enrolled && !this.router.url.includes('/auth/setup-mfa')) {
        await this.router.navigateByUrl('/auth/setup-mfa');
        return;
      }

      // Check for saved return URL
      const savedUrl = sessionStorage.getItem('auth_return_url');
      if (savedUrl && savedUrl !== '/' && savedUrl !== '') {
        sessionStorage.removeItem('auth_return_url');
        await this.router.navigateByUrl(savedUrl);
        console.log('Redirecting to saved URL:', savedUrl);
      } else if (this.router.url.includes('/auth/login') || this.router.url.includes('/auth/setup-mfa') || this.router.url === '/') {
        sessionStorage.removeItem('auth_return_url');
        await this.router.navigateByUrl('/home');
        console.log('Redirecting to /home');
      }
    } catch (error) {
      this.handleLoginError(error);
    }
  }

  /**
   * Whether the user signed up with the email/password provider
   * (as opposed to Google/Microsoft SSO)
   */
  private isEmailPasswordUser(user: FirebaseUser): boolean {
    return user.providerData.some(p => p.providerId === 'password');
  }

  /**
   * Whether current user has at least one MFA factor enrolled
   */
  hasMfaEnrolled(): boolean {
    const user = this.auth.currentUser;
    if (!user) return false;
    return multiFactor(user).enrolledFactors.length > 0;
  }

  /**
   * Begin TOTP enrollment. Returns the secret + otpauth URL for QR rendering.
   */
  async startTotpEnrollment(): Promise<{ secret: TotpSecret; otpauthUrl: string }> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    const otpauthUrl = secret.generateQrCodeUrl(user.email ?? user.uid, 'DocViewer');
    return { secret, otpauthUrl };
  }

  /**
   * Finalize TOTP enrollment with the 6-digit code from the authenticator app.
   */
  async finalizeTotpEnrollment(secret: TotpSecret, code: string, displayName = 'Authenticator'): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
    await multiFactor(user).enroll(assertion, displayName);
  }

  /**
   * Resolve a pending MFA sign-in with the TOTP code from the authenticator app.
   */
  async resolveMfaSignIn(code: string): Promise<void> {
    if (!this.pendingMfaResolver) throw new Error('No pending MFA challenge');
    const hint = this.pendingMfaResolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) throw new Error('No TOTP factor available');
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
    // Save the return URL BEFORE resolving: resolveSignIn fires the auth state
    // listener, which reads auth_return_url to decide where to redirect.
    sessionStorage.setItem('auth_return_url', this.postMfaReturnUrl);
    try {
      await this.pendingMfaResolver.resolveSignIn(assertion);
      this.pendingMfaResolver = null;
      this._mfaRequired.set(false);
    } catch (error) {
      sessionStorage.removeItem('auth_return_url');
      console.error('MFA resolve error:', error);
      throw error;
    }
  }

  /**
   * Cancel a pending MFA challenge (e.g. user navigates away).
   */
  cancelMfaChallenge(): void {
    this.pendingMfaResolver = null;
    this._mfaRequired.set(false);
  }

  /**
   * Initiate Google sign-in flow using popup
   * @param returnUrl Optional URL to redirect to after successful login
   */
  loginWithGoogle(returnUrl: string = ''): Promise<void> {
    return this.loginWithProvider(this.googleProvider, returnUrl);
  }

  /**
   * Initiate Microsoft sign-in flow using popup
   * @param returnUrl Optional URL to redirect to after successful login
   */
  loginWithMicrosoft(returnUrl: string = ''): Promise<void> {
    return this.loginWithProvider(this.microsoftProvider, returnUrl);
  }

  /**
   * Generic provider login method
   * @param provider Auth provider (Google, Microsoft, etc.)
   * @param returnUrl Optional URL to redirect to after successful login
   */
  private loginWithProvider(provider: GoogleAuthProvider | OAuthProvider, returnUrl: string = ''): Promise<void> {
    this._isLoading.set(true);
    this._error.set(null);
    this.returnUrl = returnUrl;

    // Store return URL in session storage
    sessionStorage.setItem('auth_return_url', returnUrl);

    return signInWithPopup(this.auth, provider)
      .then((result) => {
        // This part is intentionally left blank because the `onAuthStateChanged`
        // listener will handle successful authentication and redirection.
        console.log('Popup login successful, waiting for auth state change.');
      })
      .catch((error) => {
        console.error('Popup login error:', error);
        this.handleLoginError(error);
        throw error; // Rethrow to allow the calling component to handle it
      });
  }

  /**
   * Sign in with email and password
   * @param email User's email
   * @param password User's password
   * @param returnUrl Optional URL to redirect to after successful login
   */
  loginWithEmailPassword(email: string, password: string, returnUrl: string = ''): Promise<void> {
    this._isLoading.set(true);
    this._error.set(null);
    this.returnUrl = returnUrl;

    // Store return URL in session storage
    sessionStorage.setItem('auth_return_url', returnUrl);

    return signInWithEmailAndPassword(this.auth, email, password)
      .then(async result => {
        if (!result.user.emailVerified) {
          console.warn('Login attempted with unverified email — deleting account');
          await deleteUser(result.user);
          const err = { code: 'auth/email-not-verified' };
          this.handleLoginError(err);
          throw err;
        }
        console.log('Email/password login successful');
      })
      .catch(error => {
        if (error?.code === 'auth/multi-factor-auth-required') {
          console.log('MFA required — awaiting TOTP code');
          this.pendingMfaResolver = getMultiFactorResolver(this.auth, error as MultiFactorError);
          this.postMfaReturnUrl = returnUrl || '/';
          this._mfaRequired.set(true);
          this._isLoading.set(false);
          throw error;
        }
        if (error.code !== 'auth/email-not-verified') {
          console.error('Email/password login error:', error);
          this.handleLoginError(error);
        }
        throw error;
      });
  }

  /**
   * Register new user with email and password
   * @param email User's email
   * @param password User's password
   */
  registerWithEmailPassword(email: string, password: string): Promise<void> {
    this._isLoading.set(true);
    this._error.set(null);

    return createUserWithEmailAndPassword(this.auth, email, password)
      .then(async result => {
        console.log('Registration successful — sending verification email');
        await sendEmailVerification(result.user);
        await signOut(this.auth);
        console.log('Verification email sent — user signed out pending verification');
      })
      .catch(error => {
        console.error('Email/password registration error:', error);
        this.handleLoginError(error);
        throw error;
      });
  }

  /**
   * Send password reset email
   * @param email User's email
   */
  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email)
      .then(() => {
        console.log('Password reset email sent');
      })
      .catch(error => {
        console.error('Error sending password reset email:', error);
        this._error.set(error instanceof Error ? error.message : 'Failed to send reset email');
        throw error;
      });
  }

  /**
   * Backwards compatibility for existing code
   * @param returnUrl Optional URL to redirect to after successful login
   */
  login(returnUrl: string = ''): void {
    this.loginWithGoogle(returnUrl);
  }

  /**
   * Log the user out and clear authentication state
   */
  logout(): void {
    signOut(this.auth).then(() => {
      // Clear authentication state
      this.profile.set(null);
      this._isAuthenticated.set(false);

      // Reset all document-related state to prevent data leakage between users
      this.resetAllDocumentState();

      // Navigate to login page
      this.router.navigate(['auth/login']);
    }).catch((error) => {
      console.error("Error signing out: ", error);
      // Even if signOut fails, still reset the state for security
      this.profile.set(null);
      this._isAuthenticated.set(false);
      this.resetAllDocumentState();
      this.router.navigate(['auth/login']);
    });
  }

  /**
   * Reset all document-related state services
   * This prevents data leakage when switching between users
   */
  private resetAllDocumentState(): void {
    try {
      // Reset user history state (save operations, etc.)
      this.userHistoryService.reset();
      console.log('User history state reset successfully');

    } catch (error) {
      console.error('Error resetting document state:', error);
      // Continue anyway for security - better to have a broken state than leaked data
    }
  }



  /**
   * Map Firebase user to app User model
   */
  private mapFirebaseUserToUser(firebaseUser: FirebaseUser): User {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      name: firebaseUser.displayName || '',
      family_name: '',  // Not available directly from Firebase
      given_name: '',   // Not available directly from Firebase
      domain: firebaseUser.email?.split('@')[1] || '',
      picture: firebaseUser.photoURL || ''
    };
  }

  /**
   * Handle authentication errors
   */
  private handleLoginError(error: any): void {
    console.error('Authentication error:', error);
    this.profile.set(null);
    this._isAuthenticated.set(false);
    this._error.set(error instanceof Error ? error.message : 'Authentication failed');
    this._isLoading.set(false);
  }

  /**
   * Navigate to designated page after successful login
   */
  handlePostLoginRedirect(returnUrl: string = '/home'): void {
    if (this._isAuthenticated()) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.login(returnUrl);
    }
  }

  /**
   * Get the Firebase auth token
   */
  getToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const user = this.auth.currentUser;
      if (user) {
        user.getIdToken()
          .then(token => resolve(token))
          .catch(error => reject(error));
      } else {
        reject(new Error('No authenticated user'));
      }
    });
  }

  /**
   * Check if the current user is authenticated
   */
  isTokenValid(): boolean {
    return !!this.auth.currentUser;
  }

  /**
   * Manually refresh user profile data
   */
  refreshProfile(): void {
    const user = this.auth.currentUser;
    if (user) {
      try {
        this.profile.set(this.mapFirebaseUserToUser(user));
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    }
  }
}
